import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { IHttpError } from "types/http";
import ZnsCampaignService from "../services/znsCampaignService";
import {
  IZnsCampaign,
  IZnsCampaignListParams,
  IZnsCampaignListResponse,
  IZnsCampaignConfigurationListParams,
  IZnsCampaignConfigurationListResponse,
  SubmitAutoZnsCampaignPayload,
  DeactivateZnsCampaignPayload,
  ZnsCampaignType,
  ZnsCampaignTargetAudience,
  ZnsCampaignStatus,
  ZnsSendingMode,
} from "../types/znsCampaign";
import { filterCampaignConditions } from "../utils/campaignSendConditions.util";
import moment from "moment";
import { toastEmitter } from "utils/toast.util";
import {
  loadZnsCampaigns,
  loadZnsCampaignsSuccess,
  loadZnsCampaignsFailed,
  createZnsCampaign,
  submitAutoZnsCampaign,
  createZnsCampaignSuccess,
  createZnsCampaignFailed,
  downloadErrorFile,
  downloadErrorFileSuccess,
  downloadErrorFileFailed,
  loadOlCampaigns,
  loadOlCampaignsSuccess,
  loadOlCampaignsFailed,
  loadZnsCampaignConditionsSuccess,
  loadZnsCampaignConditionsFailed,
  loadZnsCampaignConditions,
  loadZnsCampaignConfigurations,
  loadZnsCampaignConfigurationsSuccess,
  loadZnsCampaignConfigurationsFailed,
  deactivateZnsCampaign,
  deactivateZnsCampaignSuccess,
  deactivateZnsCampaignFailed,
} from "./znsCampaignSlice";

/**
 * Saga worker: downloads error file and triggers browser download.
 */
function* handleDownloadErrorFile(action: PayloadAction<string>): Generator {
  try {
    const blob = (yield call(
      ZnsCampaignService.downloadErrorFile,
      action.payload
    )) as Blob;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", action.payload.split("/").pop() || "error.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    yield put(downloadErrorFileSuccess());
  } catch (error) {
    yield put(downloadErrorFileFailed(error as IHttpError));
  }
}

/**
 * Saga worker: fetches OL campaigns list and dispatches success or failure.
 */
function* handleLoadOlCampaigns(action: PayloadAction<any>): Generator {
  try {
    const data = (yield call(
      ZnsCampaignService.getOlCampaigns,
      action.payload
    )) as any;
    yield put(loadOlCampaignsSuccess(data));
  } catch (error) {
    yield put(loadOlCampaignsFailed(error as IHttpError));
  }
}

/**
 * Saga worker: fetches ZNS campaigns list and dispatches success or failure.
 */
function* handleLoadZnsCampaigns(
  action: PayloadAction<IZnsCampaignListParams>
): Generator {
  try {
    const data = (yield call(
      ZnsCampaignService.getZnsCampaigns,
      action.payload
    )) as IZnsCampaignListResponse;
    yield put(loadZnsCampaignsSuccess(data));
  } catch (error) {
    yield put(loadZnsCampaignsFailed(error as IHttpError));
  }
}

function* handleLoadZnsCampaignConfigurations(
  action: PayloadAction<IZnsCampaignConfigurationListParams>,
): Generator {
  try {
    const data = (yield call(
      ZnsCampaignService.getZnsCampaignConfigurations,
      action.payload,
    )) as IZnsCampaignConfigurationListResponse;
    yield put(loadZnsCampaignConfigurationsSuccess(data));
  } catch (error) {
    const err = error as IHttpError;
    toastEmitter.show(
      (err as any)?.Message || (err as any)?.message || "Failed to load configurations.",
      "error",
    );
    yield put(loadZnsCampaignConfigurationsFailed(err));
  }
}

/**
 * Saga worker: creates a new ZNS campaign then generates notifications.
 */
function* handleCreateZnsCampaign(
  action: PayloadAction<IZnsCampaign>
): Generator {
  try {
    const campaignId = (yield call(
      ZnsCampaignService.createZnsCampaign,
      action.payload
    )) as string;
    if (action.payload.type === ZnsCampaignType.MANUAL) {
      yield call(ZnsCampaignService.generateNotifications, campaignId);
      yield put(createZnsCampaignSuccess());
    } else {
      yield put(createZnsCampaignSuccess());
    }
  } catch (error) {
    yield put(createZnsCampaignFailed(error as IHttpError));
  }
}

function* handleSubmitAutoZnsCampaign(
  action: PayloadAction<SubmitAutoZnsCampaignPayload>,
): Generator {
  try {
    const p = action.payload;
    const send = p.sendConditions;
    const filteredCampaignConfigs = filterCampaignConditions(
      send?.campaignConfigs || [],
    );

    const sendingMode = send?.cron
      ? ZnsSendingMode.CRON_SCHEDULED
      : ZnsSendingMode.EVENT_TRIGGERED;

    const campaign: IZnsCampaign = {
      name: p.campaignName,
      description: "",
      znsTemplateId: p.znsTemplateId,
      type: ZnsCampaignType.AUTO,
      targetAudience:
        p.mode === "customer"
          ? ZnsCampaignTargetAudience.CUSTOMER
          : ZnsCampaignTargetAudience.OUTLET,
      status: ZnsCampaignStatus.ACTIVE,
      znsConfiguration: {
        ...(p.recipientFilePath ? { recipientFilePath: p.recipientFilePath } : {}),
        sendingMode,
        ...(send?.cron && send.cron !== ""
          ? { cronExpression: send.cron }
          : {}),
        activeFrom: moment().toISOString(),
        ...(send?.endDateIso && send.endDateIso !== ""
          ? { activeTo: send.endDateIso }
          : {}),
        rules:
          filteredCampaignConfigs.length > 0
            ? filteredCampaignConfigs.map((campaignConfig, index) => ({
                loyaltyCampaignId: campaignConfig.campaignId,
                loyaltyCampaignName: campaignConfig.campaignName,
                ...(campaignConfig.logicalOperator
                  ? { logicalOperator: campaignConfig.logicalOperator }
                  : {}),
                displayOrder: index,
                conditions: campaignConfig.conditions.map(
                  (cond: any, conditionIndex: number) => ({
                    fieldName: cond.fieldName,
                    comparisonOperator: cond.comparisonOperator,
                    value: cond.value || undefined,
                    ...(cond.logicalOperator
                      ? { logicalOperator: cond.logicalOperator }
                      : {}),
                    displayOrder: conditionIndex,
                  }),
                ),
              }))
            : undefined,
      },
    };

    yield call(ZnsCampaignService.createZnsCampaign, campaign);
    yield put(createZnsCampaignSuccess());
  } catch (error) {
    yield put(createZnsCampaignFailed(error as IHttpError));
  }
}

function* handleLoadZnsCampaignConditions(): Generator {
  try {
    const data = (yield call(ZnsCampaignService.getZnsCampaignConditions)) as any[];
    yield put(loadZnsCampaignConditionsSuccess(data));
  } catch (error) {
    yield put(loadZnsCampaignConditionsFailed(error as IHttpError));
  }
}

function* handleDeactivateZnsCampaign(
  action: PayloadAction<DeactivateZnsCampaignPayload>,
): Generator {
  const { znsCampaignId, configurationId, refetchParams } = action.payload;
  try {
    yield call(ZnsCampaignService.updateZnsCampaignConfiguration, znsCampaignId, {
      configurationId,
      isCurrent: false,
    });
    yield call(
      ZnsCampaignService.updateZnsCampaignStatus,
      znsCampaignId,
      ZnsCampaignStatus.DEACTIVE,
    );
    toastEmitter.show("ZNS campaign deactivated.", "success");
    yield put(deactivateZnsCampaignSuccess());
    yield put(loadZnsCampaignConfigurations(refetchParams));
  } catch (error) {
    const err = error as IHttpError;
    toastEmitter.show(
      (err as any)?.Message ||
        (err as any)?.message ||
        "Failed to deactivate ZNS campaign.",
      "error",
    );
    yield put(deactivateZnsCampaignFailed(err));
  }
}

export default function* znsCampaignSaga() {
  yield takeLatest(loadZnsCampaigns.toString(), handleLoadZnsCampaigns);
  yield takeLatest(
    loadZnsCampaignConfigurations.toString(),
    handleLoadZnsCampaignConfigurations,
  );
  yield takeLatest(deactivateZnsCampaign.toString(), handleDeactivateZnsCampaign);
  yield takeLatest(loadZnsCampaignConditions.toString(), handleLoadZnsCampaignConditions);
  yield takeLatest(
    submitAutoZnsCampaign.toString(),
    handleSubmitAutoZnsCampaign,
  );
  yield takeLatest(createZnsCampaign.toString(), handleCreateZnsCampaign);
  yield takeLatest(downloadErrorFile.toString(), handleDownloadErrorFile);
  yield takeLatest(loadOlCampaigns.toString(), handleLoadOlCampaigns);
}
